import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { doc, setDoc, collection, getDoc, updateDoc } from 'firebase/firestore';
import { getAge, isPlausibleDateOfBirth, MIN_SIGNUP_AGE } from '../utils/age';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  function signup(email, password, birthday) {
    if (!isPlausibleDateOfBirth(birthday)) {
      const err = new Error('Please enter a valid date of birth.');
      err.code = 'auth/invalid-date-of-birth';
      return Promise.reject(err);
    }
    if (getAge(birthday) < MIN_SIGNUP_AGE) {
      const err = new Error(`You must be at least ${MIN_SIGNUP_AGE} years old to create an account.`);
      err.code = 'auth/underage';
      return Promise.reject(err);
    }
    return createUserWithEmailAndPassword(auth, email, password)
      .then(async (userCredential) => {
        const user = userCredential.user;
        // Create user doc in Firestore
        await setDoc(doc(db, 'users', user.uid), {
          email: user.email,
          displayName: user.displayName || '',
          birthday,
          familyIds: [],
          role: 'user', // Default role for new users
          capabilities: ['recorder', 'listener'], // User capabilities
          superAdmin: false, // Default super admin status
          recordingScope: 'personal', // 'personal' = record my own copy; 'family' = only verses no one recorded
          defaultRecordPoint: 'last-recorded', // where Record starts with no active plan: 'last-recorded' | 'whole-bible'
          defaultListenPoint: 'last-listened', // where Listen starts with no active plan: 'last-listened' | 'whole-bible'
          bibleTranslation: 'bsb', // Default for new users: Berean Standard Bible ('web' = World English Bible)
          createdAt: new Date(),
          lastPassageRead: null,
          lastPassageRecorded: null,
          notificationPreferences: {
            newRecordings: true, // Default to enabled for new recordings
            frequency: 'daily' // Default to daily digest (every recording vs daily)
          },
          playbackPreferences: {
            trimSilence: false // Default off; trims dead air at verse start/end on Listen
          }
        });
        // Create empty subcollections for recordedPassages and notes
        // Firestore does not allow empty subcollections, so add a dummy doc and delete it later if needed
        await setDoc(doc(db, 'users', user.uid, 'recordedPassages', '_init'), { init: true });
        await setDoc(doc(db, 'users', user.uid, 'notes', '_init'), { init: true });
        return userCredential;
      });
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function logout() {
    return signOut(auth);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Load additional user data from Firestore
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            // Grandfather existing users who have families but predate the onboardingCompleted flag
            if (!userData.onboardingCompleted && userData.familyIds?.length > 0) {
              userData.onboardingCompleted = true;
              updateDoc(doc(db, 'users', user.uid), { onboardingCompleted: true }).catch(() => {});
            }
            // Merge Firebase Auth user with Firestore data
            setCurrentUser({
              ...user,
              ...userData
            });
          } else {
            // Auth account exists but no Firestore doc — create it now
            const userData = {
              email: user.email,
              displayName: user.displayName || '',
              familyIds: [],
              role: 'user',
              capabilities: ['recorder', 'listener'],
              superAdmin: false,
              recordingScope: 'personal',
              defaultRecordPoint: 'last-recorded',
              defaultListenPoint: 'last-listened',
              bibleTranslation: 'bsb',
              createdAt: new Date(),
              lastPassageRead: null,
              lastPassageRecorded: null,
              notificationPreferences: { newRecordings: true, frequency: 'daily' },
              playbackPreferences: { trimSilence: false },
            };
            setDoc(doc(db, 'users', user.uid), userData).catch(() => {});
            setCurrentUser({ ...user, ...userData });
          }
        } catch (error) {
          console.error('Error loading user data:', error);
          setCurrentUser(user);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const refreshUserData = async () => {
    if (currentUser?.uid) {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCurrentUser({
            ...currentUser,
            ...userData
          });
        }
      } catch (error) {
        console.error('Error refreshing user data:', error);
      }
    }
  };

  const value = {
    currentUser,
    loading,
    signup,
    login,
    logout,
    refreshUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
} 